/**
 * 개인 대시보드 페이지
 *
 * V2: 휴일 표시 + 출근 버튼 제어 + 휴일근무 유도 CTA
 * - 오늘이 휴일이면 상단에 휴일 배지 표시
 * - 휴일 + 휴일근무 미승인 시 출근 차단 + 휴일근무 신청 CTA 표시
 * - 휴일 + 휴일근무 승인 시 정상 출근 가능
 *
 * V158.7: 출근 버튼 UX 개선
 * - 근로일정 미승인 시에도 버튼은 클릭 가능 (disabled 제거)
 * - 클릭 시 안내 모달로 유도 (왜 안되는지 사용자 인지 가능)
 *
 * V158.6: 출퇴근 즉각 반영 UX 개선
 * - 출근/퇴근 버튼 클릭 시 즉각 UI 반영
 * - CF 응답으로 받은 attendance 객체로 상태 직접 업데이트
 * - 실패 시 이전 상태로 자동 롤백
 *
 * V158.5: 근로일정 미승인 시 출근 불가 선조건 추가
 *
 * [0] 선조건: 근로일정 승인 여부 + 휴일 여부
 * - workSchedule?.base 존재 여부로 판단
 * - 휴일 시 holidayWork 승인 여부 추가 판단
 * - 미승인 시 버튼 클릭하면 안내 모달 노출
 *
 * [1행] Hero 영역 (3카드 고정)
 * - 출근 카드: "출근" 라벨 + 버튼(항상 클릭 가능) + 출근시간
 * - 퇴근 카드: "퇴근" 라벨 + 버튼(근무중시만 활성) + 퇴근시간
 * - 상태 카드: 미출근/근무중/퇴근완료 배지
 *
 * [2행] Action 카드 (3카드 고정)
 * - [1] 역할별: 팀원→내 승인요청 / 승인자→승인대기
 * - [2] 이번달 근로현황 (소정/실제)
 * - [3] 이번달 연장근무
 *
 * 제약: 기존 API/데이터/로직 변경 없음 (UI 구조만 변경)
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTodayAttendance } from '../../shared/hooks/useAttendance';
import { useAttendanceMutations } from '../../shared/hooks/useAttendanceMutations';
// V162.1: Cloud Function 기반 단일 데이터 소스로 변경 (Firestore 직접 조회 제거)
import { useApprovedWorkSchedule } from '../../shared/hooks/useApprovedWorkSchedule';
import { computeEffectiveWorkEndMinutes } from '../../shared/utils/attendanceCapping';
import { useMonthlyAttendances } from '../../shared/hooks/useAttendance';
import { useOvertimeSummary } from '../../shared/hooks/useOvertimeRequests';
import { useMyApprovalRequests } from '../../shared/hooks/useApprovalRequests';
import { usePendingApprovals, usePendingApprovalCount } from '../../shared/hooks/useApprovalRequests';
import {
  Calendar,
  TrendingUp,
  FileText,
  CheckSquare,
  Briefcase,
  AlertCircle,
  X,
  Sun,  // V2: 휴일 아이콘
  ChevronLeft,   // V55.49: 월 네비게이션
  ChevronRight,  // V55.49: 월 네비게이션
} from 'lucide-react';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useCalendarHolidays, CompanyCode } from '../../shared/hooks/useHolidays';  // V233.2: 전직원용 휴일 조회 (getHolidays→getCalendarHolidays)
import {
  formatTimeByCompanyCode,
  formatMonthByCompanyCode,
  getCurrentYearByCompanyCode,
  getCurrentMonthByCompanyCode,
  formatTodayByCompanyCode,  // V2: SSoT 날짜
} from '../../shared/utils/dateUtils';

// ============================================
// 메인 컴포넌트
// ============================================

export function WorkMyDashboardPage() {
  const navigate = useNavigate();
  // V170.1: companyCode 기반 현재 연월 계산
  const { user } = useCurrentUser();
  const companyCode = user?.profile?.companyCode;
  const currentYear = getCurrentYearByCompanyCode(companyCode);
  const currentMonth = getCurrentMonthByCompanyCode(companyCode);

  // ============================================
  // V55.49: 월 네비게이션 state
  // - 근무요약 / 출퇴근 기록 두 영역은 항상 같은 달 기준
  // ============================================
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // V55.49: 이전 월로 이동
  const handlePrevMonth = useCallback(() => {
    if (selectedMonth === 1) {
      setSelectedYear((prev) => prev - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth((prev) => prev - 1);
    }
  }, [selectedMonth]);

  // V55.49: 다음 월로 이동 (현재 월보다 미래는 불가)
  const handleNextMonth = useCallback(() => {
    // 현재 월이면 다음으로 이동 불가
    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return;
    }
    if (selectedMonth === 12) {
      setSelectedYear((prev) => prev + 1);
      setSelectedMonth(1);
    } else {
      setSelectedMonth((prev) => prev + 1);
    }
  }, [selectedYear, selectedMonth, currentYear, currentMonth]);

  // V55.49: 다음 월 버튼 비활성화 여부
  const isNextMonthDisabled = selectedYear === currentYear && selectedMonth === currentMonth;

  // V55.49: 선택된 월 문자열 (useApprovedWorkSchedule용)
  const selectedMonthStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  // V162.1: Cloud Function 기반 단일 데이터 소스
  // Firestore 직접 조회(useWorkScheduleV152) 제거 → CF(getMyWorkSchedule)로 통일
  // V55.49: 현재 월 → 선택된 월 기준으로 변경
  const monthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  // V5.16: monthlyWorkStats 추가 (Backend SSoT)
  const {
    workSchedule,
    monthlyWorkStats,  // V5.16: Backend 계산 결과
    approvedLeaves,    // V81.1: 오늘 반차/퍼힐 판단용
    hasApproved: hasApprovedWorkSchedule,
    isLoading: workScheduleLoading,
  } = useApprovedWorkSchedule(monthStr);

  // V55.49: 선택된 월 기준 근로일정 (근무요약용)
  const {
    monthlyWorkStats: selectedMonthlyWorkStats,
    isLoading: selectedWorkScheduleLoading,
  } = useApprovedWorkSchedule(selectedMonthStr);

  // V55.49: 선택된 월 기준 출퇴근 기록 (근무요약/출퇴근기록용)
  const { attendances, isLoading: attendancesLoading } = useMonthlyAttendances(
    undefined,        // employeeId: hook 내부에서 currentUser 사용
    selectedYear,     // V55.49: 선택된 연도
    selectedMonth     // V55.49: 선택된 월
  );

  // V55.49: 선택된 월 기준 연장근무 집계 (근무요약용)
  const { summary: overtimeSummary } = useOvertimeSummary(undefined, selectedYear, selectedMonth);

  // V55.52.1: 현재월 고정 훅 ("이번달 근로현황", "이번달 연장근무"용)
  const { attendances: currentMonthAttendances } = useMonthlyAttendances(
    undefined,
    currentYear,
    currentMonth
  );
  const { summary: currentMonthOvertimeSummary } = useOvertimeSummary(undefined, currentYear, currentMonth);

  // 오늘 출퇴근 상태 (V158.6: setAttendance 추가로 즉각 반영 지원)
  const {
    attendance: todayAttendance,
    isLoading: todayLoading,
    setAttendance: setTodayAttendance
  } = useTodayAttendance();
  const { checkIn, checkOut, isProcessing } = useAttendanceMutations();

  // 승인요청 건수 (기존 Hook 사용)
  const { requests: myRequests } = useMyApprovalRequests({ status: 'pending' });
  const { requests: pendingApprovals } = usePendingApprovals({ status: 'pending' });
  // V64: 대시보드 카드용 전체 pending 건수 (페이지네이션 무관, BE count() aggregation)
  const { count: pendingApprovalCount } = usePendingApprovalCount();

  // V158.5: 근로일정 미승인 안내 모달 상태
  const [showScheduleAlert, setShowScheduleAlert] = useState(false);

  // V2: 휴일근무 신청 안내 모달 상태
  const [showHolidayWorkAlert, setShowHolidayWorkAlert] = useState(false);

  // V2: 오늘 날짜 (회사 타임존 기준 SSoT)
  const todayDateStr = formatTodayByCompanyCode(companyCode);

  // V233.2: 전직원용 휴일 조회 (getCalendarHolidays — 로그인 사용자 전원 허용)
  const { holidays, isLoading: holidaysLoading } = useCalendarHolidays(
    (companyCode as CompanyCode) || 'KR',
    currentYear
  );

  // V2: 오늘이 휴일인지 판단
  const todayHoliday = useMemo(() => {
    if (!holidays || holidays.length === 0) return null;
    return holidays.find((h) => h.date === todayDateStr && h.status === 'active') || null;
  }, [holidays, todayDateStr]);

  const isHoliday = !!todayHoliday;

  // V2: 휴일근무 승인 여부 확인 (myRequests 중 holidayWork + approved + date 일치)
  // 참고: BE에서 checkIn 시 hasApprovedHolidayWork로 이중 검증함
  const { requests: allMyRequests } = useMyApprovalRequests({ year: currentYear });
  const hasApprovedHolidayWork = useMemo(() => {
    if (!allMyRequests || !isHoliday) return false;
    return allMyRequests.some(
      (r) =>
        (r.type === 'holidayWork' || r.type === 'weekendWork') &&
        r.status === 'approved' &&
        r.requestData?.date === todayDateStr
    );
  }, [allMyRequests, isHoliday, todayDateStr]);

  // V158.4: 승인자 여부 판단 (pendingApprovals가 조회되면 승인자)
  // usePendingApprovals는 approverId == auth.uid로 쿼리하므로 결과가 있으면 승인자
  const isApprover = useMemo(() => {
    return pendingApprovals !== undefined && pendingApprovals !== null;
  }, [pendingApprovals]);

  // V55.49: 근무일 수 (선택된 월 기준, Backend SSoT 사용)
  const workDaysCount = selectedMonthlyWorkStats?.workingDays ?? 0;

  // V55.49: 월간 근로시간 계산 (선택된 월 기준 - 근무요약용)
  const workHours = useMemo(() => {
    const actualMinutes = attendances?.reduce((sum, att) => sum + att.actualWorkMinutes, 0) ?? 0;
    const overtimeMinutes = overtimeSummary?.totalMinutes || 0;

    return {
      regular: selectedMonthlyWorkStats?.regularHours ?? 0,
      actual: Math.floor(actualMinutes / 60),
      overtime: Math.floor(overtimeMinutes / 60),
    };
  }, [attendances, selectedMonthlyWorkStats, overtimeSummary]);

  // V55.52.1: 현재월 근로시간 계산 ("이번달 근로현황", "이번달 연장근무"용)
  const currentMonthWorkHours = useMemo(() => {
    const actualMinutes = currentMonthAttendances?.reduce((sum, att) => sum + att.actualWorkMinutes, 0) ?? 0;
    const overtimeMinutes = currentMonthOvertimeSummary?.totalMinutes || 0;

    return {
      regular: monthlyWorkStats?.regularHours ?? 0,
      actual: Math.floor(actualMinutes / 60),
      overtime: Math.floor(overtimeMinutes / 60),
    };
  }, [currentMonthAttendances, monthlyWorkStats, currentMonthOvertimeSummary]);

  // 월간 지각 횟수 계산
  const lateCount = useMemo(() => {
    if (!attendances) return 0;
    return attendances.filter((att) => att.isLate).length;
  }, [attendances]);

  // 실제 출근한 일수
  const actualWorkDays = useMemo(() => {
    if (!attendances) return 0;
    return attendances.filter((att) => att.checkInTime).length;
  }, [attendances]);

  // 근무 상태 계산
  const workStatus = useMemo(() => {
    if (!todayAttendance?.checkInTime) {
      return { status: 'not_checked_in', label: '미출근', color: '#DC2626', bgColor: '#FEE2E2' };
    }
    if (todayAttendance.checkOutTime) {
      return { status: 'checked_out', label: '퇴근완료', color: '#16A34A', bgColor: '#DCFCE7' };
    }
    return { status: 'working', label: '근무중', color: '#0891b2', bgColor: '#cffafe' };
  }, [todayAttendance]);

  // V81.6: 퇴근 예정 시간 전 여부 (확인 팝업용)
  //  - attendance.plannedEndTime 은 체크인 시점 스냅샷 → 신뢰 불가
  //  - 오늘 승인된 모든 휴가(반차/퍼힐/hourly)를 interval 통합 처리
  //  - BE `checkOutAttendance`의 `computeEffectiveWorkEndMinutes` 와 동일 알고리즘
  //    (공통 유틸: shared/utils/attendanceCapping.ts)
  const isBeforeEndTime = useMemo(() => {
    if (!todayAttendance?.plannedEndTime) return false;

    const baseStart =
      workSchedule?.base?.startTime ||
      (todayAttendance as any).plannedStartTime ||
      '09:00';
    const baseEnd =
      workSchedule?.base?.endTime || todayAttendance.plannedEndTime;

    const todaysLeaves = (approvedLeaves || []).filter(
      (l) => l.date === todayDateStr,
    );

    const effectiveEndMin = computeEffectiveWorkEndMinutes(
      baseStart,
      baseEnd,
      todaysLeaves,
    );

    const now = new Date();
    const end = new Date();
    end.setHours(
      Math.floor(effectiveEndMin / 60),
      effectiveEndMin % 60,
      0,
      0,
    );
    return now < end;
  }, [todayAttendance, approvedLeaves, todayDateStr, workSchedule]);

  // V170.1: 출근 시간 포맷 (회사 타임존 기준)
  const checkInTimeStr = useMemo(() => {
    return formatTimeByCompanyCode(todayAttendance?.checkInTime, companyCode);
  }, [todayAttendance, companyCode]);

  // V170.1: 퇴근 시간 포맷 (회사 타임존 기준)
  const checkOutTimeStr = useMemo(() => {
    return formatTimeByCompanyCode(todayAttendance?.checkOutTime, companyCode);
  }, [todayAttendance, companyCode]);

  // V163: 출근 버튼 클릭 핸들러 (즉각 반영 + 근로일정 선조건 검사 + V2 휴일 검사)
  const handleCheckIn = async () => {
    // V163: 로딩 중이면 팝업 표시하지 않음 (데이터 확정 전)
    if (workScheduleLoading || holidaysLoading) {
      return;
    }

    // 근로일정이 승인되지 않았으면 안내 후 return
    if (!hasApprovedWorkSchedule) {
      setShowScheduleAlert(true);
      return;
    }

    // V61: 휴일근무 승인 검증은 BE(checkInAttendance → hasApprovedHolidayWork)에 위임
    // FE allMyRequests는 pageSize=20 제한으로 승인 건을 누락할 수 있어 FE 게이트 제거
    // BE는 approvalRequests/overtimeRequests 직접 쿼리로 정확히 판정함
    // 힌트 텍스트(L694)는 UX 안내용으로 유지

    // 이전 상태 저장 (롤백용)
    const previousAttendance = todayAttendance;

    try {
      // CF 호출 및 응답 받기
      const result = await checkIn();

      if (result.success && result.attendance) {
        // V158.6: CF 응답으로 받은 attendance로 즉각 상태 업데이트
        setTodayAttendance(result.attendance);
      } else if (!result.success) {
        // 실패 시 에러 로그 (롤백 불필요 - 상태 변경 안됨)
        console.error('[V158.6] 출근 실패:', result.error);
        alert(result.error || '출근 처리에 실패했습니다.');
      }
    } catch (err: any) {
      // V61: BE 휴일근무 미승인 에러 → 친절한 안내 모달로 전환
      const errorMessage = err?.message || '';
      if (isHoliday && errorMessage.includes('휴일')) {
        setShowHolidayWorkAlert(true);
        return;
      }
      // 기타 예외 시 이전 상태로 롤백
      console.error('[V158.6] 출근 예외:', err);
      setTodayAttendance(previousAttendance);
      alert(errorMessage || '출근 처리 중 오류가 발생했습니다.');
    }
  };

  // V158.6: 퇴근 버튼 클릭 핸들러 (즉각 반영)
  // V64: 퇴근시간 전이면 확인 팝업 후 진행
  const handleCheckOut = async () => {
    if (isBeforeEndTime) {
      const confirmed = window.confirm('아직 퇴근시간이 아닙니다. 그래도 퇴근하시겠습니까?');
      if (!confirmed) return;
    }

    // 이전 상태 저장 (롤백용)
    const previousAttendance = todayAttendance;

    try {
      // CF 호출 및 응답 받기
      const result = await checkOut();

      if (result.success && result.attendance) {
        // V158.6: CF 응답으로 받은 attendance로 즉각 상태 업데이트
        // 퇴근 시에는 기존 출근 정보를 유지하면서 퇴근 정보만 추가
        if (previousAttendance) {
          setTodayAttendance({
            ...previousAttendance,
            ...result.attendance,
            checkInTime: previousAttendance.checkInTime, // 출근시간 유지
            isLate: previousAttendance.isLate,           // 지각여부 유지
          });
        } else {
          // 이론상 도달 불가 (출근 없이 퇴근 불가)
          setTodayAttendance(result.attendance);
        }
      } else if (!result.success) {
        // 실패 시 에러 로그 (롤백 불필요 - 상태 변경 안됨)
        console.error('[V158.6] 퇴근 실패:', result.error);
        alert(result.error || '퇴근 처리에 실패했습니다.');
      }
    } catch (err) {
      // 예외 발생 시 이전 상태로 롤백
      console.error('[V158.6] 퇴근 예외:', err);
      setTodayAttendance(previousAttendance);
      alert('퇴근 처리 중 오류가 발생했습니다.');
    }
  };

  // V158.7: 출근 버튼 비활성화 조건 (네트워크 처리 중만)
  // 근로일정 미승인/휴일은 disabled로 막지 않고, 클릭 시 모달로 안내
  const isCheckInDisabled = isProcessing || todayLoading || workScheduleLoading || holidaysLoading;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '34px' }}>
      {/* 페이지 제목 */}
      <h1 style={{
        fontSize: '24px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: isHoliday ? '16px' : '46px',
      }}>
        개인 대시보드
      </h1>

      {/* V2: 오늘이 휴일인 경우 휴일 배지 표시 */}
      {isHoliday && todayHoliday && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: '#FEF3C7',
            border: '1px solid #FCD34D',
            borderRadius: '10px',
            marginBottom: '24px',
          }}
        >
          <Sun size={20} color="#F59E0B" />
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: '600', color: '#92400E' }}>
              오늘은 {todayHoliday.name}입니다
            </span>
            {!hasApprovedHolidayWork && (
              <span style={{ marginLeft: '8px', fontSize: '13px', color: '#B45309' }}>
                (출근하려면 휴일근무 승인이 필요합니다)
              </span>
            )}
            {hasApprovedHolidayWork && (
              <span style={{ marginLeft: '8px', fontSize: '13px', color: '#065F46' }}>
                ✓ 휴일근무 승인 완료
              </span>
            )}
          </div>
          {!hasApprovedHolidayWork && workStatus.status === 'not_checked_in' && (
            <button
              onClick={() => navigate('/work/my-approvals?type=holidayWork')}
              style={{
                padding: '8px 16px',
                background: '#F59E0B',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              휴일근무 신청하기
            </button>
          )}
        </div>
      )}

      {/* ========================================== */}
      {/* V158.7: 근로일정 신청 안내 모달 */}
      {/* ========================================== */}
      {showScheduleAlert && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowScheduleAlert(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#FEF3C7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={24} color="#F59E0B" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                  근로일정 신청이 필요합니다
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                  출근 처리를 위해 이번 달 근로일정을 먼저 신청하고 승인받아야 합니다.
                </p>
              </div>
              <button
                onClick={() => setShowScheduleAlert(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#9CA3AF',
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowScheduleAlert(false);
                  navigate('/work/schedule');
                }}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                근로일정 신청하러 가기
              </button>
              <button
                onClick={() => setShowScheduleAlert(false)}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* V2: 휴일근무 신청 안내 모달 */}
      {/* ========================================== */}
      {showHolidayWorkAlert && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowHolidayWorkAlert(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#FEF3C7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Sun size={24} color="#F59E0B" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                  오늘은 {todayHoliday?.name}입니다
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                  휴일에 출근하려면 먼저 휴일근무를 신청하고 승인받아야 합니다.
                </p>
              </div>
              <button
                onClick={() => setShowHolidayWorkAlert(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#9CA3AF',
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowHolidayWorkAlert(false);
                  navigate('/work/my-approvals?type=holidayWork');
                }}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                휴일근무 신청하기
              </button>
              <button
                onClick={() => setShowHolidayWorkAlert(false)}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* V158.5: 1행 Hero 영역 (3카드 고정) */}
      {/* 좌: 출근 / 중: 퇴근 / 우: 현재상태 */}
      {/* ========================================== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {/* [1] 출근 카드 */}
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '160px',
          }}
        >
          <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500', marginBottom: '16px' }}>
            출근
          </div>

          {/* 버튼 영역 */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginBottom: '16px' }}>
            {workStatus.status === 'not_checked_in' ? (
              <>
                <button
                  onClick={handleCheckIn}
                  disabled={isCheckInDisabled}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: isCheckInDisabled ? '#D1D5DB' : '#374151',
                    color: isCheckInDisabled ? '#9CA3AF' : 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: isCheckInDisabled ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                  }}
                >
                  {isProcessing ? '처리중...' : '출근하기'}
                </button>
                {/* V158.7: 근로일정 미승인 시 힌트 텍스트 */}
                {!hasApprovedWorkSchedule && !workScheduleLoading && (
                  <div style={{
                    fontSize: '11px',
                    color: '#9CA3AF',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}>
                    ※ 근로일정 승인 후 출근 가능합니다
                  </div>
                )}
                {/* V2: 휴일 + 휴일근무 미승인 시 힌트 텍스트 */}
                {hasApprovedWorkSchedule && isHoliday && !hasApprovedHolidayWork && !holidaysLoading && (
                  <div style={{
                    fontSize: '11px',
                    color: '#B45309',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}>
                    ※ 오늘은 휴일입니다. 휴일근무 승인 후 출근 가능합니다
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: '#F3F4F6',
                  color: '#9CA3AF',
                  borderRadius: '10px',
                  textAlign: 'center',
                }}
              >
                출근 완료
              </div>
            )}
          </div>

          {/* 출근 시간 표시 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>출근시간 :</span>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {checkInTimeStr}
            </span>
            {todayAttendance?.isLate && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                지각
              </span>
            )}
          </div>
        </div>

        {/* [2] 퇴근 카드 */}
        <div
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '160px',
          }}
        >
          <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500', marginBottom: '16px' }}>
            퇴근
          </div>

          {/* 버튼 영역 */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            {workStatus.status === 'working' ? (
              <button
                onClick={handleCheckOut}
                disabled={isProcessing}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: isProcessing ? '#D1D5DB' : '#4B5563',
                  color: isProcessing ? '#9CA3AF' : 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.7 : 1,
                }}
              >
                {isProcessing ? '처리중...' : '퇴근하기'}
              </button>
            ) : workStatus.status === 'checked_out' ? (
              <div
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: '#F3F4F6',
                  color: '#9CA3AF',
                  borderRadius: '10px',
                  textAlign: 'center',
                }}
              >
                퇴근 완료
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: '#F9FAFB',
                  color: '#D1D5DB',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '1px dashed #E5E7EB',
                }}
              >
                출근 후 가능
              </div>
            )}
          </div>

          {/* 퇴근 시간 표시 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>퇴근시간 :</span>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {checkOutTimeStr}
            </span>
          </div>
        </div>

        {/* [3] 현재 상태 카드 */}
        <div
          style={{
            background: workStatus.bgColor,
            borderRadius: '16px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid ${workStatus.color}20`,
            position: 'relative',
            overflow: 'hidden',
            minHeight: '160px',
          }}
        >
          {/* 배경 데코레이션 */}
          <div
            style={{
              position: 'absolute',
              right: '-30px',
              top: '-30px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: `${workStatus.color}10`,
            }}
          />

          <div style={{ position: 'relative', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>현재 상태</div>
            <div
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: workStatus.color,
              }}
            >
              {workStatus.label}
            </div>
          </div>
        </div>
      </div>

      {/* ========================================== */}
      {/* V158.4: 2행 Action 카드 (3카드 고정) */}
      {/* [1] 역할별(팀원:내승인요청/승인자:승인대기) */}
      {/* [2] 이번달 근로현황 */}
      {/* [3] 이번달 연장근무 */}
      {/* ========================================== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {/* [1] 역할별 카드: 팀원→내 승인요청 / 승인자→승인대기 */}
        {isApprover && pendingApprovalCount > 0 ? (
          // 승인자: 승인대기 카드 (V64: 페이지네이션 무관 전체 건수)
          <ActionCard
            icon={<CheckSquare size={24} color="#16A34A" />}
            iconBg="#DCFCE7"
            label="승인대기"
            sublabel="처리 필요"
            value={pendingApprovalCount}
            unit="건"
            valueColor={pendingApprovalCount > 0 ? '#DC2626' : undefined}
            onClick={() => navigate('/work/approvals')}
          />
        ) : (
          // 팀원: 내 승인요청 카드
          <ActionCard
            icon={<FileText size={24} color="#2563EB" />}
            iconBg="#DBEAFE"
            label="내 승인요청"
            sublabel="대기중"
            value={myRequests?.length || 0}
            unit="건"
            onClick={() => navigate('/work/my-approvals')}
          />
        )}

        {/* [2] 이번달 근로현황 (공통) */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#E0E7FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Briefcase size={24} color="#4F46E5" />
            </div>
          </div>

          <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>
            이번달 근로현황
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-gray-400)', marginBottom: '12px' }}>
            {currentMonth}월
          </div>

          {/* V55.52.1: currentMonthWorkHours 사용 (현재월 고정) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>소정</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--color-gray-800)', marginLeft: '4px' }}>
                {currentMonthWorkHours.regular}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginLeft: '2px' }}>h</span>
            </div>
            <div style={{ color: 'var(--color-gray-300)' }}>/</div>
            <div>
              <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>실제</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#0891b2', marginLeft: '4px' }}>
                {currentMonthWorkHours.actual}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginLeft: '2px' }}>h</span>
            </div>
          </div>
        </div>

        {/* [3] 이번달 연장근무 (공통) - V55.52.1: currentMonthWorkHours 사용 */}
        <ActionCard
          icon={<TrendingUp size={24} color="#F59E0B" />}
          iconBg="#FEF3C7"
          label="이번달 연장근무"
          sublabel={`${currentMonth}월`}
          value={currentMonthWorkHours.overtime}
          unit="시간"
        />
      </div>

      {/* ========================================== */}
      {/* V55.49: 월 네비게이션 UI */}
      {/* ========================================== */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',  // V55.49.1: 좌측 정렬
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <button
          onClick={handlePrevMonth}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={20} color="#374151" />
        </button>
        <span
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            minWidth: '140px',
            textAlign: 'center',
          }}
        >
          {selectedYear}년 {selectedMonth}월
        </span>
        <button
          onClick={handleNextMonth}
          disabled={isNextMonthDisabled}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            background: isNextMonthDisabled ? '#F3F4F6' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isNextMonthDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <ChevronRight size={20} color={isNextMonthDisabled ? '#9CA3AF' : '#374151'} />
        </button>
      </div>

      {/* ========================================== */}
      {/* V55.49: 월간 요약 카드 (선택된 월 기준) */}
      {/* ========================================== */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--color-gray-800)',
            marginBottom: '20px',
          }}
        >
          {selectedYear}년 {selectedMonth}월 근무 요약
        </h2>

        {/* V55.49: 5열로 변경 (연장근무 추가) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
          }}
        >
          <SummaryItem label="근무일수" value={actualWorkDays} total={workDaysCount} unit="일" />
          <SummaryItem label="소정근로" value={workHours.regular} unit="시간" />
          <SummaryItem label="실제근로" value={workHours.actual} unit="시간" />
          <SummaryItem
            label="연장근무"
            value={workHours.overtime}
            unit="시간"
            valueColor={workHours.overtime > 0 ? '#F59E0B' : undefined}
          />
          <SummaryItem
            label="지각"
            value={lateCount}
            unit="회"
            valueColor={lateCount > 0 ? '#DC2626' : '#16A34A'}
          />
        </div>
      </div>

      {/* ========================================== */}
      {/* V55.49: 월간 근무 상세 테이블 (선택된 월 기준) */}
      {/* ========================================== */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <h2
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--color-gray-800)',
            marginBottom: '16px',
          }}
        >
          {selectedYear}년 {selectedMonth}월 출퇴근 기록
        </h2>

        {attendancesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            로딩 중...
          </div>
        ) : attendances && attendances.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>날짜</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>출근</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>퇴근</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>지각</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>근무형태</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-600)' }}>근무시간</th>
                </tr>
              </thead>
              <tbody>
                {attendances.filter((att) => {
                  // V81: 미래일 auto-generated 휴일근무 attendance 숨김
                  // 실제 checkIn이 없고, isAutoGenerated이고, 미래일이면 표시 안 함
                  if (att.isAutoGenerated && !att.checkInTime && att.date > todayDateStr) return false;
                  return true;
                }).map((attendance) => (
                  <tr key={attendance.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: 'var(--color-gray-700)' }}>
                      {attendance.date}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: 'var(--color-gray-600)' }}>
                      {/* V170.1: companyCode 기반 타임존으로 시간 표시 */}
                      {/* V302: 자동 생성 휴일근무 attendance는 checkInLocalTime 폴백 */}
                      {attendance.checkInTime
                        ? formatTimeByCompanyCode(attendance.checkInTime, companyCode)
                        : (attendance.checkInLocalTime || '-')}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: 'var(--color-gray-600)' }}>
                      {/* V170.1: companyCode 기반 타임존으로 시간 표시 */}
                      {/* V302: 자동 생성 휴일근무 attendance는 checkOutLocalTime 폴백 */}
                      {/* V279: 연장근무 시 plannedEndTime + overtimeMinutes로 실효 퇴근시각 표시 */}
                      {(() => {
                        const baseTime = attendance.checkOutTime
                          ? formatTimeByCompanyCode(attendance.checkOutTime, companyCode)
                          : (attendance.checkOutLocalTime || '-');

                        // 연장근무가 있고 holidayWork가 아닌 경우 실효 퇴근시각 계산
                        if (attendance.overtimeMinutes > 0 && !attendance.isHolidayWork && attendance.plannedEndTime) {
                          const [h, m] = attendance.plannedEndTime.split(':').map(Number);
                          const totalMin = h * 60 + m + attendance.overtimeMinutes;
                          const endH = Math.floor(totalMin / 60) % 24;
                          const endM = totalMin % 60;
                          const effectiveEnd = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                          return (
                            <>
                              {baseTime}
                              <span style={{ fontSize: '12px', color: 'var(--color-orange-600)', marginLeft: '4px' }}>
                                (→{effectiveEnd})
                              </span>
                            </>
                          );
                        }

                        return baseTime;
                      })()}
                    </td>
                    {/* V279: 지각 칼럼 — isLate 여부만 표시 (근무형태 분리) */}
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {attendance.isLate ? (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: 'var(--color-red-100)',
                            color: 'var(--color-red-700)',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          지각
                        </span>
                      ) : null}
                    </td>
                    {/* V279: 근무형태 칼럼 — 휴일근무/연장근무 표시 */}
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {attendance.isHolidayWork ? (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: 'var(--color-blue-100)',
                            color: 'var(--color-blue-700)',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          휴일근무
                        </span>
                      ) : attendance.overtimeMinutes > 0 ? (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: 'var(--color-orange-100)',
                            color: 'var(--color-orange-700)',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          연장근무
                        </span>
                      ) : null}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', color: 'var(--color-gray-700)' }}>
                      {Math.floor(attendance.actualWorkMinutes / 60)}시간 {attendance.actualWorkMinutes % 60}분
                      {attendance.overtimeMinutes > 0 && (
                        <span style={{ fontSize: '12px', color: 'var(--color-orange-600)', marginLeft: '4px' }}>
                          (+{Math.floor(attendance.overtimeMinutes / 60) > 0
                            ? `${Math.floor(attendance.overtimeMinutes / 60)}시간${attendance.overtimeMinutes % 60 > 0 ? ` ${attendance.overtimeMinutes % 60}분` : ''}`
                            : `${attendance.overtimeMinutes % 60}분`})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            {selectedYear}년 {selectedMonth}월 출퇴근 기록이 없습니다.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// 액션 카드 컴포넌트
// ============================================

interface ActionCardProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  sublabel?: string;
  value: number;
  unit: string;
  valueColor?: string;
  onClick?: () => void;
}

function ActionCard({ icon, iconBg, label, sublabel, value, unit, valueColor, onClick }: ActionCardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.2s',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--color-gray-500)' }}>{label}</div>
        {sublabel && <div style={{ fontSize: '11px', color: 'var(--color-gray-400)', marginTop: '2px' }}>{sublabel}</div>}
        <div style={{ marginTop: '8px' }}>
          <span style={{ fontSize: '28px', fontWeight: '700', color: valueColor || 'var(--color-gray-800)' }}>
            {value}
          </span>
          <span style={{ fontSize: '14px', color: 'var(--color-gray-500)', marginLeft: '4px' }}>{unit}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 요약 아이템 컴포넌트
// ============================================

interface SummaryItemProps {
  label: string;
  value: number;
  total?: number;
  unit: string;
  valueColor?: string;
}

function SummaryItem({ label, value, total, unit, valueColor }: SummaryItemProps) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '8px' }}>{label}</div>
      <div>
        <span style={{ fontSize: '24px', fontWeight: '700', color: valueColor || 'var(--color-gray-800)' }}>
          {value}
        </span>
        {total !== undefined && (
          <span style={{ fontSize: '14px', color: 'var(--color-gray-400)' }}> / {total}</span>
        )}
        <span style={{ fontSize: '14px', color: 'var(--color-gray-500)', marginLeft: '4px' }}>{unit}</span>
      </div>
    </div>
  );
}
